import React, { useState, useEffect, useMemo } from 'react';
import { Text, View, TouchableOpacity } from 'react-native';
import { useUnistyles } from 'react-native-unistyles';
import { StyleSheet } from 'react-native-unistyles';
import Input from '@/components/General/inputs/Input';
import InputSection from '@/components/General/inputs/InputSection';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { saveRole } from "@/data/roles";
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import PermissionsEditor from '@/components/Roles/PermissionsEditor';
import ToggleRow from '@/components/General/inputs/ToggleRow';
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';
import Callout from '@/components/General/Callout';
import { useEditScreenStore } from '@/data/store/useEditScreenStore';
import { redirectToDefault } from '@/utils/constants/linking';
import { useRouter, useLocalSearchParams } from 'expo-router';
import ModalWrapper from '@/components/General/ModalWrapper';
import Unicon from '@/components/General/Unicon';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { areStringsPopulated } from '@/utils/InputValidator';
import Toast from 'react-native-toast-message';
import useOrganizationEntitlements from '@/utils/hooks/useOrganizationEntitlements';
import { isFreeOrg } from '@/utils/organizationEntitlements';
import PlanRestrictedScreen from '@/components/General/PlanRestrictedScreen';

const EMPTY_DRAFT = {};

export default function AddRoleModal() {
    const { entitlements } = useOrganizationEntitlements();
    if (isFreeOrg(entitlements)) return <PlanRestrictedScreen />;
    return <AddRoleModalInner />;
}

function AddRoleModalInner() {
    const { id, fromStoreKey, folderID } = useLocalSearchParams();
    const storeKey = id || fromStoreKey || "new"

    const [ editorPaneOpen, setEditorPaneOpen ] = useState(false)
    const [ key, setKey ] = useState(1)
    const [ isRoleDynamic, setIsRoleDynamic ] = useState(false)

    useUnistyles();
    const queryClient = useQueryClient()
    const router = useRouter()

    const {data: loggedInUser} = Query.people.loggedInUser()
    const {data: permissions } = Query.permissions.all()
    const {data: roleToEdit} = Query.roles.byId({id, options: {enabled: !!id, placeholderData: {}}})
    const {data: roleFoldersData } = Query.roleFolders.all({options: {enabled: !id && !!folderID}})

    // Filter out locked permissions - these cannot be granted to any role.
    // Account owners are exempt: RLS permits them to assign locked permissions,
    // so they can see and toggle them in the editor.
    const canEditLockedPermissions = loggedInUser?.isAccountOwner;
    const availablePermissions = useMemo(() => 
        permissions?.filter(p => !p.locked || canEditLockedPermissions) || [], 
        [permissions, canEditLockedPermissions]
    );

    // Check if the role being edited has any locked permissions.
    // Account owners can still edit roles with locked permissions.
    const hasLockedPermissions = useMemo(() => 
        !canEditLockedPermissions && (roleToEdit?.Permissions?.some(p => p.locked) || false),
        [roleToEdit?.Permissions, canEditLockedPermissions]
    );

    const setEditScreenData = useEditScreenStore(s => s.setEditScreenData)
    const removeEditScreenData = useEditScreenStore(s => s.removeEditScreenData)
    const roleDetails = useEditScreenStore(s => s.AddRoleModal?.[storeKey]) || EMPTY_DRAFT

    const isAccessControlRole = roleDetails.isAccessControl || false;
    const canCreateRoles = loggedInUser?.can('Create Roles');
    const canEditRoles = loggedInUser?.can('Edit Roles');
    const isRoleOwner = loggedInUser?.ManagingRoles?.some(r => r.id === id);

    // Admins can always edit. Non-admins with Edit Roles can edit grouping roles only.
    // For creating, non-admins with Create Roles can create grouping roles only.
    const canEditRoleDetails = id
        ? loggedInUser?.isAdmin || (!isAccessControlRole && canEditRoles)
        : loggedInUser?.isAdmin || canCreateRoles;

    // Mirrors the RolePeople RLS policy: admin OR Role Owner (on access control roles)
    // OR user with 'Edit Roles' (on grouping roles). When false, skip member writes
    // entirely so a non-admin viewer saving the role doesn't hit RLS denials.
    const canEditMembers = loggedInUser?.isAdmin || isRoleOwner || canEditRoleDetails;

    // Non-admin (not Role Owner) opening an access control role → locked
    const showAccessControlLocked = id && isAccessControlRole && !loggedInUser?.isAdmin && !isRoleOwner;

    const editorPaneHeight = useSharedValue(0);
    const editorPaneContentHeight = React.useRef(0);
    const editorPaneStyle = useAnimatedStyle(() => ({height: withTiming(editorPaneHeight.value, {duration: 300})}) )

    const onOwnerPaneLayout = (e) => {
        const h = e.nativeEvent.layout.height;
        if (h === 0) return;
        editorPaneContentHeight.current = h;
        if (editorPaneOpen) {
            editorPaneHeight.value = h;
        }
    };


    const setRoleDetails = (details) => {
        setEditScreenData("AddRoleModal", storeKey, details)
    }

    useEffect(() => {
        if(roleToEdit && roleDetails?.id !== roleToEdit?.id){
            setRoleDetails({
                ...roleToEdit,
                folderID: folderID || null,
                originalValues: roleToEdit,
            });

            setIsRoleDynamic(!!roleToEdit?.QuickFilter);
        }
    }, [roleToEdit]);

    // When creating a new role from within a folder, pre-populate the Folder
    // dropdown from the folderID route param so it matches the current context.
    useEffect(() => {
        if (!id && folderID && roleFoldersData?.allFolders && !roleDetails?.folderID) {
            const folder = roleFoldersData.allFolders.find(f => f.id === folderID);
            if (folder) {
                setRoleDetails({
                    folderID: folder.id,
                    Folder: folder,
                });
            }
        }
    }, [id, folderID, roleFoldersData, roleDetails?.folderID]);



    const saveRoleMutation = useMutation({
        mutationFn: saveRole,
        onSuccess: (data) => {
            queryClient.invalidateQueries({queryKey: QueryKeys.roleFolders.all()});
            queryClient.invalidateQueries({queryKey: QueryKeys.roles.all()});
            queryClient.invalidateQueries({queryKey: QueryKeys.permissions.all()});

            data.People?.items?.forEach(i => {
                queryClient.invalidateQueries({queryKey: QueryKeys.people.byId(i.Person?.id)});
                if(i.Person?.id == loggedInUser?.id){
                    queryClient.invalidateQueries({queryKey: QueryKeys.people.loggedInUser()});
                }
            })
    
            removeEditScreenData("AddRoleModal", storeKey)
            redirectToDefault(router, "AddRoleModal")
        },
    })


    const setPermission = useMemo(() => (permissionToChange, new_value = null) => {
        const newPermissions = [...(roleDetails?.Permissions || [])];
        const currentPermIndex = newPermissions?.findIndex(n => n.name == permissionToChange);

        if(new_value === null || (new_value && currentPermIndex == -1) || (!new_value && currentPermIndex > -1)){
            if(!new_value || currentPermIndex > -1){
                newPermissions.splice(currentPermIndex, 1);
            }
            else{
                newPermissions.push(availablePermissions?.find(n => n.name == permissionToChange))
            }
        }

        setRoleDetails({
            Permissions: newPermissions
        })
    }, [roleDetails?.Permissions, availablePermissions]);

    const setPermissionsBatch = useMemo(() => (permissionNames, new_value) => {
        const newPermissions = [...(roleDetails?.Permissions || [])];
        
        if (new_value) {
            permissionNames.forEach(name => {
                if (!newPermissions.find(p => p.name === name)) {
                    const perm = availablePermissions?.find(n => n.name === name);
                    if (perm) newPermissions.push(perm);
                }
            });
        } else {
            permissionNames.forEach(name => {
                const idx = newPermissions.findIndex(p => p.name === name);
                if (idx > -1) newPermissions.splice(idx, 1);
            });
        }

        setRoleDetails({
            Permissions: newPermissions
        });
    }, [roleDetails?.Permissions, availablePermissions]);




    const managerCards = useMemo(() => roleDetails?.People?.map((rolePerson) => {
        return <ToggleRow
            key={rolePerson.id}
            label={rolePerson.firstName + " " + rolePerson.lastName}
            disabled={!canEditRoleDetails}
            onValueChange={() => {
                setKey(old => old + 1)
                rolePerson.canManage = !rolePerson.canManage
            }}
            value={rolePerson.canManage}
        />
    }), [key, roleDetails?.People]);
    
    // If role has locked permissions, show warning and prevent editing
    if (id && hasLockedPermissions) {
        return (
            <ModalWrapper
                onCancel={() => removeEditScreenData("AddRoleModal", storeKey)} 
                title="Role Locked"
            >
                <View style={{padding: 20, alignItems: 'center'}}>
                    <Callout type="error" message="This role contains locked permissions and cannot be edited. Locked permissions are system-level permissions that cannot be granted or modified." />
                </View>
            </ModalWrapper>
        );
    }

    // If non-admin (not Role Owner) opens an access control role, show locked message
    if (showAccessControlLocked) {
        return (
            <ModalWrapper
                onCancel={() => removeEditScreenData("AddRoleModal", storeKey)}
                title="Access Control Role"
            >
                <View style={{padding: 20, alignItems: 'center'}}>
                    <Callout type="info" message="This is an Access Control role. Only admins can create or edit Access Control roles." />
                </View>
            </ModalWrapper>
        );
    }

    return (
        <ModalWrapper
            loading={saveRoleMutation.isPending} 
            onCancel={() => removeEditScreenData("AddRoleModal", storeKey)} 
            onConfirm={() => {
                const isValid = areStringsPopulated([roleDetails.name]);
                if (!isValid) {
                    Toast.show({
                        type: 'error',
                        text2: 'The Role must have a name!',
                    });
                    return;
                }

                const { originalValues, ...cleanDetails } = roleDetails;
                saveRoleMutation.mutate({details: cleanDetails, originalValues, membersOnly: !canEditRoleDetails, canEditPermissions: loggedInUser?.isAdmin, canEditLockedPermissions, canEditMembers})
            }} 
            title={id ? "Update Role" : "New Role"}
        >
        
            <KeyboardAwareScrollView bottomOffset={100} keyboardShouldPersistTaps="handled" contentContainerStyle={{alignItems:"center"}}>
                <View style={styles.scrollContent}>
                    { canEditRoleDetails &&
                        <InputSection title="Role Details">
                            <Input
                                title="Name"
                                maxLength={120}
                                value={roleDetails.name}
                                onUpdate={(new_value) => {
                                    setRoleDetails({
                                        name: new_value
                                    })
                                }}
                            />

                            <Input
                                title="Description"
                                multiline
                                value={roleDetails.description}
                                onUpdate={(new_value) => {
                                    setRoleDetails({
                                        description: new_value
                                    })
                                }}
                            />

                            <SheetSelector type="roleFolders" title="Folder" fromScreen={`roles/create`} fromStoreKey={storeKey} addScreenName="roles/folders/create" value={roleDetails.Folder} onChange={(selected_item) => {
                                setRoleDetails({
                                    folderID: selected_item?.id,
                                    Folder: selected_item
                                });
                            }} />

                            <ToggleRow
                                label="Hide This Role"
                                description="By default, users will be able to see this role and its members to assign it to resources in the app. This option will hide this role so that only admins and Role Owners (managers) of this role can see it."
                                onValueChange={() => setRoleDetails({
                                    isHidden: !roleDetails.isHidden
                                })}
                                value={roleDetails.isHidden || false}
                            />

                            <ToggleRow
                                label="Access Control Role"
                                description="Grouping roles are for organizing people. Access Control roles grant permissions to members — only admins can manage them."
                                disabled={!loggedInUser?.isAdmin}
                                onValueChange={() => setRoleDetails({
                                    isAccessControl: !roleDetails.isAccessControl
                                })}
                                value={roleDetails.isAccessControl || false}
                            />
                        </InputSection>
                    }

                    <InputSection title="Members" description={isRoleDynamic
                        ? "A dynamic role will use a Quick Filter to determine who is in the role at any given time. This allows people to move in and out of the role as their profile is updated."
                        : "Select people to be a part of this Role. These people will be used for anything that this Role is chosen for accross the app."
                    }>
                        { canEditRoleDetails &&
                            <ToggleRow
                                label="Enable Dynamic Role"
                                onValueChange={() => {
                                    setRoleDetails(isRoleDynamic ? {quickFilterID: null, QuickFilter: null, People: undefined} : {People: [], quickFilterID: null, QuickFilter: null})
                                    setIsRoleDynamic(!isRoleDynamic)
                                }}
                                value={isRoleDynamic}
                            />
                        }

                        { isRoleDynamic
                            ? <SheetSelector type="quickFilters" title="Filter" fromScreen={`roles/create`} fromStoreKey={storeKey} value={roleDetails.QuickFilter} onChange={(selected_item) => {
                                setRoleDetails({
                                    quickFilterID: selected_item?.id,
                                    QuickFilter: selected_item
                                });
                            }} />

                            : <View>
                                <SheetSelector type="people" title="Members" fromScreen={`roles/create`} fromStoreKey={storeKey} addScreenName="people/create" multiple value={roleDetails.People || []} onChange={(selected_item) => {
                                    setRoleDetails({
                                        People: selected_item
                                    });
                                }} />

                                { roleDetails.People?.length > 0 &&
                                    <TouchableOpacity style={styles.ownerToggle} onPress={() => {
                                        editorPaneHeight.value = !editorPaneOpen ? editorPaneContentHeight.current : 0
                                        setEditorPaneOpen(old => !old)
                                    }}>
                                        <Unicon name={!editorPaneOpen ? "chevron-down-outline" : "chevron-up-outline"} size={18} colorVariant="textSecondary"/>
                                        <Text style={styles.ownerToggleText}>Role Owner(s)</Text>
                                        <Unicon name={!editorPaneOpen ? "chevron-down-outline" : "chevron-up-outline"} size={18} colorVariant="textSecondary"/>
                                    </TouchableOpacity>
                                }
                                {/* Ghost view: measures real content height on native without being clipped */}
                                <View pointerEvents="none" style={{position:'absolute', opacity:0, left:0, right:0}} onLayout={onOwnerPaneLayout}>
                                    <Callout type="info" message="The Role Owner(s) will be able to manage members of this Role. They will not be able to change permissions."/>
                                    {managerCards}
                                </View>
                                <Animated.View style={[editorPaneStyle, {overflow:"hidden"}]}>
                                    <Callout type="info" message="The Role Owner(s) will be able to manage members of this Role. They will not be able to change permissions."/>
                                    {managerCards}
                                </Animated.View>
                            </View>
                        }
                    </InputSection>

                    { loggedInUser?.isAdmin && !!roleDetails.isAccessControl &&
                        <View style={{marginTop: 12}}>
                            <Text style={styles.sectionTitle}>Permissions</Text>
                            <Text style={styles.sectionDescription}>Any permissions given to the Role will be given to all users in the Role. A user with multiple Roles will inherit all permissions from each Role.</Text>
                            <PermissionsEditor
                                permissions={availablePermissions}
                                selectedPermissions={roleDetails.Permissions || []}
                                disabled={!loggedInUser?.isAdmin}
                                onPermissionChange={(permName, newValue) => setPermission(permName, newValue)}
                                onPermissionBatch={setPermissionsBatch}
                            />
                        </View>
                    }
                </View>
            </KeyboardAwareScrollView>
        </ModalWrapper> 
    );
}

const styles = StyleSheet.create(({colors, fonts, spacing}) => ({
    scrollContent: {
        width: '100%',
        paddingBottom: 20,
    },
    ownerToggle: {
        alignItems: 'center',
        flexDirection: 'row',
        justifyContent: 'center',
        paddingVertical: 10,
        gap: 8,
    },
    ownerToggleText: {
        fontSize: 13,
        color: colors.textSecondary,
        fontFamily: fonts.heading,
        textTransform: 'uppercase',
    },
    sectionTitle: {
        fontSize: 13,
        color: colors.textSecondary,
        fontFamily: fonts.bold,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        marginBottom: spacing.xs,
    },
    sectionDescription: {
        fontSize: 13,
        lineHeight: 18,
        color: colors.textTertiary,
        marginBottom: spacing.sm,
    },
}));